import { Alert, Button, EmptyState, Input, LoadingState, Modal, useToast } from '@orbind/ui'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { PERMISSAO_DO_CONECTOR_ROTULO } from '@orbind/core'
import {
  baixarDoConector,
  quandoFoi,
  useGerarToken,
  useMeuConector,
  useMeusEventos,
  useRevogarToken,
} from '../features/ai-connector/useConectorDeIa.ts'
import './conectar-ao-claude.css'

/**
 * Conectar ao Claude (SPEC-20261103-1818) — a tela da PESSOA.
 *
 * Três passos, nesta ordem: gerar o token, instalar o conector no Claude que
 * ela usa (Desktop, Code ou claude.ai), e pronto. O token aparece UMA vez,
 * numa janela, com botão de copiar — depois disso só os 4 últimos caracteres.
 *
 * Estados que a tela cobre: carregando; erro; a tela não está no plano; a
 * pessoa não foi liberada (diz quem libera); liberada sem token; liberada
 * com tokens.
 */

const copiar = async (texto: string) => navigator.clipboard.writeText(texto)

/** O prompt da rotina de aviso (SPEC-20261103-1822): o mesmo da skill e do prompt `avisos` do conector. */
const TEXTO_DO_AVISO =
  'Chame resumo_do_dia no conector Orbind e me avise em até 6 linhas: atrasados, o que vence hoje e o mais importante. Se não houver nada atrasado nem vencendo, diga só isso. Não altere nada.'

export function ConectarAoClaudePage() {
  const navigate = useNavigate()
  const toast = useToast()
  const consulta = useMeuConector()
  const eventos = useMeusEventos()
  const gerar = useGerarToken()
  const revogar = useRevogarToken()

  const [nome, setNome] = useState('')
  const [tokenNovo, setTokenNovo] = useState<{ token: string; name: string } | null>(null)
  const [baixando, setBaixando] = useState<'pacote.mcpb' | 'skill.zip' | null>(null)

  const baixar = async (caminho: 'pacote.mcpb' | 'skill.zip', nomeDoArquivo: string) => {
    setBaixando(caminho)
    try {
      await baixarDoConector(caminho, nomeDoArquivo)
    } catch (e) {
      toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível baixar.' })
    } finally {
      setBaixando(null)
    }
  }

  if (consulta.isPending) {
    return (
      <div className="cac">
        <LoadingState variant="detail" label="Carregando o conector" />
      </div>
    )
  }
  if (consulta.isError) {
    return (
      <div className="cac">
        <Alert
          tone="danger"
          title="Não foi possível carregar o conector"
          action={
            <Button size="sm" icon="fas fa-redo" onClick={() => void consulta.refetch()}>
              Tentar de novo
            </Button>
          }
        >
          {consulta.error instanceof Error ? consulta.error.message : 'Tente novamente em instantes.'}
        </Alert>
      </div>
    )
  }

  const me = consulta.data

  const gerarToken = () => {
    const n = nome.trim() || 'Claude'
    gerar.mutate(n, {
      onSuccess: (r) => {
        setTokenNovo({ token: r.token, name: n })
        setNome('')
      },
      onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível gerar.' }),
    })
  }

  const comandoComToken = tokenNovo ? me.comandoDoClaudeCode.replace('<token>', tokenNovo.token) : me.comandoDoClaudeCode

  return (
    <div className="cac">
      <div className="cac__inner">
        <header className="cac__head">
          <h1 className="cac__title">
            <i className="fas fa-robot" aria-hidden /> Conectar ao Claude
          </h1>
          <p className="cac__sub">
            Use o Orbind de dentro do Claude: pergunte o que você tem para hoje, atualize, comente, conclua e crie
            Itens de Trabalho na conversa. O conector age como você — vê o que você vê e faz só o que o
            administrador liberou.
          </p>
        </header>

        {!me.disponivel ? (
          <EmptyState
            icon="fas fa-lock"
            title="Conectores de IA não estão no seu plano"
            description="Fale com o responsável pela conta para ampliar o plano do workspace."
          />
        ) : !me.liberado ? (
          <EmptyState
            icon="fas fa-user-lock"
            title="Você ainda não foi liberado"
            description="Quem administra o workspace libera pessoas na tela Conectores de IA, na Administração."
            action={
              me.admin ? (
                <Button variant="primary" icon="fas fa-users-cog" onClick={() => navigate('/admin/ai-connectors')}>
                  Abrir Conectores de IA
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <section className="cac__passo">
              <h2 className="cac__h2">
                <span className="cac__num">1</span> Gere um token pessoal
              </h2>
              <p className="cac__texto">
                Um token por aparelho ou programa ("Claude Desktop do notebook", "Claude Code"). Ele aparece uma única
                vez; se perder, revogue e gere outro. Conexões feitas pelo claude.ai (OAuth) aparecem na mesma lista e
                se revogam do mesmo jeito.
              </p>
              <div className="cac__gerar">
                <Input
                  placeholder="Nome do token (ex.: Claude Desktop)"
                  value={nome}
                  maxLength={120}
                  onChange={(e) => setNome(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') gerarToken()
                  }}
                  aria-label="Nome do token"
                />
                <Button variant="primary" icon="fas fa-key" disabled={gerar.isPending} onClick={gerarToken}>
                  Gerar token
                </Button>
              </div>
              {me.tokens.length === 0 ? (
                <p className="cac__vazio">Você ainda não tem tokens.</p>
              ) : (
                <ul className="cac__tokens">
                  {me.tokens.map((t) => (
                    <li key={t.id} className="cac__token">
                      <span className="cac__token-nome">
                        {t.name}
                        {t.kind === 'oauth' ? <span className="cac__token-tipo"> OAuth</span> : null}
                      </span>
                      <code className="cac__token-fim">orb_…{t.last4}</code>
                      <span className="cac__token-meta">
                        criado {quandoFoi(t.createdAt)} · último uso {quandoFoi(t.lastUsedAt)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        icon="fas fa-ban"
                        disabled={revogar.isPending}
                        onClick={() =>
                          revogar.mutate(t.id, {
                            onSuccess: () => toast.push({ tone: 'success', message: `Token "${t.name}" revogado.` }),
                            onError: (e) =>
                              toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Não foi possível revogar.' }),
                          })
                        }
                      >
                        Revogar
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="cac__passo">
              <h2 className="cac__h2">
                <span className="cac__num">2</span> Instale o conector no Claude que você usa
              </h2>
              <div className="cac__opcoes">
                <article className="cac__opcao">
                  <h3 className="cac__h3">
                    <i className="fas fa-desktop" aria-hidden /> Claude Desktop (aplicativo)
                  </h3>
                  <Button
                    variant="secondary"
                    icon="fas fa-download"
                    disabled={baixando !== null}
                    onClick={() => void baixar('pacote.mcpb', 'orbind.mcpb')}
                  >
                    Baixar orbind.mcpb
                  </Button>
                  {/* o caminho inteiro, passo a passo (SPEC-20261103-2014): quem
                      só viu "abra o arquivo" se perdeu na primeira instalação */}
                  <ol className="cac__passos">
                    <li>Baixe o <code className="cac__inline">orbind.mcpb</code> (fica na pasta Downloads).</li>
                    <li>
                      Abra o <strong>Claude Desktop</strong> → clique no seu nome (canto inferior esquerdo) →{' '}
                      <strong>Configurações</strong> (ou <code className="cac__inline">Ctrl+,</code>).
                    </li>
                    <li>
                      Menu <strong>Extensões</strong> → role até o fim → <strong>Configurações avançadas</strong> →{' '}
                      <strong>Instalar extensão…</strong> → escolha o <code className="cac__inline">orbind.mcpb</code>.
                    </li>
                    <li>
                      Na janela "Orbind", cole o <strong>token</strong> gerado no passo 1 e clique em{' '}
                      <strong>Instalar</strong>. Deixe a extensão ligada.
                    </li>
                    <li>Abra uma conversa nova e pergunte: "o que tenho para hoje no Orbind?"</li>
                  </ol>
                  <p className="cac__texto cac__texto--nota">
                    Dois cliques no arquivo também funcionam se o Windows souber abri-lo com o Claude; se ele perguntar
                    "com qual aplicativo", use o caminho acima. Sem o menu Extensões, atualize o Claude Desktop em
                    Ajuda › Verificar atualizações.
                  </p>
                  {/* o Desktop guarda a lista de ferramentas do pacote instalado
                      (SPEC-20261103-2229): sem reinstalar, ferramenta nova não aparece */}
                  <p className="cac__texto cac__texto--nota">
                    <strong>Pacote versão {me.versao}.</strong> Quando o conector ganha ferramentas novas, a versão
                    sobe: baixe o arquivo de novo e instale pelo mesmo caminho, por cima do anterior. O Claude Desktop
                    só enxerga as ferramentas do pacote que está instalado; em Extensões › Orbind dá para conferir a
                    versão.
                  </p>
                </article>
                <article className="cac__opcao">
                  <h3 className="cac__h3">
                    <i className="fas fa-terminal" aria-hidden /> Claude Code
                  </h3>
                  <ol className="cac__passos">
                    <li>Gere o token no passo 1 e copie o comando abaixo.</li>
                    <li>
                      Abra um terminal na pasta do seu projeto e cole o comando, trocando{' '}
                      <code className="cac__inline">&lt;token&gt;</code> pelo token (no aviso do token gerado há um
                      botão que já copia o comando pronto).
                    </li>
                    <li>
                      No Claude Code, rode <code className="cac__inline">/mcp</code> para conferir que "orbind" está
                      conectado.
                    </li>
                  </ol>
                  <pre className="cac__codigo">{me.comandoDoClaudeCode}</pre>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="fas fa-copy"
                    onClick={() =>
                      void copiar(me.comandoDoClaudeCode).then(() => toast.push({ tone: 'success', message: 'Comando copiado.' }))
                    }
                  >
                    Copiar comando
                  </Button>
                </article>
                <article className="cac__opcao">
                  <h3 className="cac__h3">
                    <i className="fas fa-globe" aria-hidden /> claude.ai e organização
                  </h3>
                  <p className="cac__texto">Sem token: você autoriza com a sua conta do Orbind.</p>
                  <ol className="cac__passos">
                    <li>
                      No <strong>claude.ai</strong> (navegador) ou no Claude Desktop: clique no seu nome →{' '}
                      <strong>Configurações</strong> → <strong>Conectores</strong>.
                    </li>
                    <li>
                      Clique em <strong>Adicionar conector personalizado</strong> (fim da lista).
                    </li>
                    <li>
                      Nome: <strong>Orbind</strong>. URL do servidor MCP remoto: o endereço abaixo (copie). Deixe os campos
                      avançados em branco e clique em <strong>Adicionar</strong>.
                    </li>
                    <li>
                      Clique em <strong>Conectar</strong>: o Orbind abre numa aba pedindo para você{' '}
                      <strong>autorizar</strong> (escolha o workspace). Volte ao Claude — pronto.
                    </li>
                  </ol>
                  <pre className="cac__codigo">{me.url}</pre>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="fas fa-copy"
                    onClick={() => void copiar(me.url).then(() => toast.push({ tone: 'success', message: 'Endereço copiado.' }))}
                  >
                    Copiar endereço
                  </Button>
                  <p className="cac__texto cac__texto--nota">
                    Numa organização Claude for Work, o administrador dela cadastra este endereço uma vez para todo
                    mundo; cada pessoa só autoriza no primeiro uso.
                  </p>
                </article>
              </div>
            </section>

            <section className="cac__passo">
              <h2 className="cac__h2">
                <span className="cac__num">3</span> Skill (opcional) e o que o Claude pode fazer por você
              </h2>
              <p className="cac__texto">
                O conector já diz ao Claude como se comportar. A skill é o manual completo, útil no Claude Code:
                descompacte em <code className="cac__inline">~/.claude/skills/</code>.
              </p>
              <Button
                variant="secondary"
                icon="fas fa-download"
                disabled={baixando !== null}
                onClick={() => void baixar('skill.zip', 'orbind-skill.zip')}
              >
                Baixar a skill
              </Button>
              <ul className="cac__permissoes" aria-label="Permissões liberadas">
                {me.permissoes.map((p) => (
                  <li key={p}>
                    <i className="fas fa-check" aria-hidden /> {PERMISSAO_DO_CONECTOR_ROTULO[p]}
                  </li>
                ))}
              </ul>
            </section>

            <section className="cac__passo">
              <h2 className="cac__h2">
                <span className="cac__num">4</span> Avisos e a tela do Item na conversa
              </h2>
              <p className="cac__texto">
                Peça "me mostra o {'{código}'}" e a tela do Item abre dentro da conversa, com Concluir e Comentar
                (no claude.ai e no Claude Desktop). Para ser avisado sem pedir, crie uma rotina agendada no Claude
                — por exemplo todo dia às 8h — com este texto:
              </p>
              <pre className="cac__codigo">{TEXTO_DO_AVISO}</pre>
              <Button
                variant="secondary"
                size="sm"
                icon="fas fa-copy"
                onClick={() => void copiar(TEXTO_DO_AVISO).then(() => toast.push({ tone: 'success', message: 'Texto do aviso copiado.' }))}
              >
                Copiar texto do aviso
              </Button>
            </section>

            <section className="cac__passo">
              <h2 className="cac__h2">Minhas últimas ações pelo Claude</h2>
              {eventos.isPending ? (
                <LoadingState variant="list" label="Carregando" />
              ) : eventos.isError ? (
                <p className="cac__vazio">Não foi possível carregar as ações.</p>
              ) : eventos.data.eventos.length === 0 ? (
                <p className="cac__vazio">Nenhuma ação de escrita ainda.</p>
              ) : (
                <ul className="cac__eventos">
                  {eventos.data.eventos.map((ev) => (
                    <li key={ev.id} className="cac__evento">
                      <span className="cac__evento-quando">{quandoFoi(ev.createdAt)}</span>
                      {ev.workItemId ? (
                        <button type="button" className="cac__link" onClick={() => navigate(`/work-items/${ev.workItemId}`)}>
                          {ev.summary}
                        </button>
                      ) : (
                        <span>{ev.summary}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>

      <Modal
        open={tokenNovo !== null}
        onClose={() => setTokenNovo(null)}
        title={`Token "${tokenNovo?.name ?? ''}" gerado`}
        footer={
          <Button variant="primary" onClick={() => setTokenNovo(null)}>
            Já guardei
          </Button>
        }
      >
        <div className="cac__novo">
          <p className="cac__texto">
            Copie agora. Por segurança ele <strong>não aparece de novo</strong>: o Orbind guarda só uma impressão
            dele.
          </p>
          <pre className="cac__codigo cac__codigo--token">{tokenNovo?.token}</pre>
          <div className="cac__novo-acoes">
            <Button
              variant="primary"
              icon="fas fa-copy"
              onClick={() =>
                tokenNovo && void copiar(tokenNovo.token).then(() => toast.push({ tone: 'success', message: 'Token copiado.' }))
              }
            >
              Copiar token
            </Button>
            <Button
              variant="secondary"
              icon="fas fa-terminal"
              onClick={() =>
                void copiar(comandoComToken).then(() => toast.push({ tone: 'success', message: 'Comando do Claude Code copiado, com o token.' }))
              }
            >
              Copiar comando do Claude Code
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
